<template>
  <SidePanel
    v-bind="$attrs"
    :is-saving="panelSaving"
    :save-error="panelSaveError"
    :save-success="panelSaveSuccess"
    :show-save="false"
    borderless
    @close="$emit('close')"
  >
    <!-- Header card: avatar/name + contact details + students/created/address.
         Pinned to the top while the rest scrolls (not while editing — the form is
         tall). Opaque dark fill = white/7% over surface-raised-dark, so scrolled
         content doesn't show through. -->
    <div
      class="-mt-3 rounded-xl border border-contact-card-border dark:border-contact-card-border-dark bg-contact-card-bg dark:bg-contact-card-bg-dark p-3 space-y-2"
      :class="editingDetails ? 'relative' : 'sticky -top-3 z-10'"
    >
      <div class="flex items-center gap-2.5">
        <div
          class="h-10 w-10 rounded-full flex-shrink-0 flex items-center justify-center text-white text-sm font-semibold"
          :style="{ background: avatarGradient(contact.name) }"
        >{{ initials(contact.name) || '?' }}</div>
        <div class="flex-1 min-w-0">
          <h3 class="font-semibold text-base text-fg-base dark:text-fg-base-dark truncate">{{ contact.name || '(No name)' }}</h3>
          <p v-if="contact.created_at" class="text-xs text-fg-faint dark:text-fg-faint-dark">Created {{ fmtUtcDate(contact.created_at) }}</p>
        </div>
        <button @click="$emit('close')" class="self-start text-fg-faint hover:text-fg-secondary dark:hover:text-fg-base-dark text-lg leading-none flex-shrink-0">&times;</button>
      </div>

      <!-- Contact details — read-only until Edit is clicked -->
      <div v-if="!editingDetails" class="flex items-start gap-2">
        <div class="min-w-0 flex-1 space-y-1">
          <p class="flex items-center gap-1.5 min-w-0">
            <EnvelopeIcon class="h-3.5 w-3.5 flex-shrink-0 text-fg-faint dark:text-fg-faint-dark" />
            <RouterLink v-if="contact.email && canEmail" :to="{ name: 'AdminEmailsInbox', query: { compose: contact.email } }" title="Send an email"
              class="truncate text-fg-secondary dark:text-fg-secondary-dark hover:underline">{{ contact.email }}</RouterLink>
            <a v-else-if="contact.email" :href="`mailto:${contact.email}`" class="truncate text-fg-secondary dark:text-fg-secondary-dark hover:underline">{{ contact.email }}</a>
            <span v-else class="text-fg-faint dark:text-fg-faint-dark">No email</span>
          </p>
          <p class="flex items-center gap-1.5">
            <PhoneIcon class="h-3.5 w-3.5 flex-shrink-0 text-fg-faint dark:text-fg-faint-dark" />
            <span :class="contact.phone ? 'text-fg-secondary dark:text-fg-secondary-dark' : 'text-fg-faint dark:text-fg-faint-dark'">{{ contact.phone || 'No phone' }}</span>
          </p>
          <p v-if="contact.aliases?.length" class="text-xs text-fg-muted dark:text-fg-muted-dark">aka {{ contact.aliases.join(', ') }}</p>
        </div>
        <button @click="startEditDetails" class="text-xs text-blue-600 dark:text-blue-400 hover:underline flex-shrink-0">Edit</button>
      </div>
      <div v-else class="space-y-2">
        <div v-for="f in DETAIL_FIELDS" :key="f.key">
          <label class="block text-xs font-medium text-fg-muted dark:text-fg-muted-dark mb-1">{{ f.label }}</label>
          <input v-model="panelDraft[f.key]" :type="f.type" :placeholder="f.placeholder" autocomplete="off"
            @keydown.enter="savePanel" @keydown.esc="cancelEditDetails"
            class="w-full px-2.5 py-1.5 rounded border border-stroke-strong dark:border-stroke-strong-dark bg-surface dark:bg-surface-muted-dark text-fg-base dark:text-fg-base-dark text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
        </div>
        <!-- Students: name / DOB / linked contact (moving one to another contact removes it from this panel) -->
        <div v-if="studentDrafts.length" class="space-y-2">
          <p class="text-xs font-medium text-fg-muted dark:text-fg-muted-dark">Students</p>
          <div v-for="s in studentDrafts" :key="s.id" class="rounded-md border border-stroke-subtle dark:border-stroke-dark p-2 space-y-1.5">
            <div class="flex gap-2">
              <div class="flex-1 min-w-0">
                <label class="block text-xs font-medium text-fg-muted dark:text-fg-muted-dark mb-1">Student Name</label>
                <input v-model="s.name" type="text" placeholder="Student name" autocomplete="off"
                  @keydown.esc="cancelEditDetails"
                  class="w-full px-2.5 py-1.5 rounded border border-stroke-strong dark:border-stroke-strong-dark bg-surface dark:bg-surface-muted-dark text-fg-base dark:text-fg-base-dark text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
              </div>
              <div class="w-36 flex-shrink-0">
                <label class="block text-xs font-medium text-fg-muted dark:text-fg-muted-dark mb-1">Date of Birth</label>
                <input v-model="s.dob" type="date"
                  class="w-full px-2.5 py-1.5 rounded border border-stroke-strong dark:border-stroke-strong-dark bg-surface dark:bg-surface-muted-dark text-fg-base dark:text-fg-base-dark text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
              </div>
            </div>
            <label class="block text-xs font-medium text-fg-muted dark:text-fg-muted-dark mb-1">Linked Contact</label>
            <Combobox v-model="s.contact" by="id">
              <div class="relative">
                <ComboboxInput
                  class="w-full pr-8 px-2.5 py-1.5 rounded border border-stroke-strong dark:border-stroke-strong-dark bg-surface dark:bg-surface-muted-dark text-fg-base dark:text-fg-base-dark text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  :displayValue="c => c?.name ?? ''"
                  @change="contactQuery = $event.target.value"
                  placeholder="Linked contact…"
                  autocomplete="off"
                />
                <ComboboxButton class="absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronUpDownIcon class="h-4 w-4 text-fg-faint" />
                </ComboboxButton>
                <ComboboxOptions @after-leave="contactQuery = ''" class="absolute z-30 mt-1 max-h-52 w-full overflow-auto rounded-md border border-stroke dark:border-stroke-dark bg-surface dark:bg-surface-dark shadow-lg text-sm">
                  <div v-if="filteredContacts.length === 0" class="px-4 py-3 text-fg-muted dark:text-fg-muted-dark">No contacts found</div>
                  <ComboboxOption v-for="c in filteredContacts" :key="c.id" :value="c" v-slot="{ active }">
                    <li :class="['flex flex-col px-3 py-2 cursor-pointer select-none', active ? 'bg-blue-600 text-white' : 'text-fg-base dark:text-fg-base-dark']">
                      <span class="font-medium truncate">{{ c.name }}</span>
                      <span v-if="c.email" :class="['text-xs truncate', active ? 'text-blue-200' : 'text-fg-faint']">{{ c.email }}</span>
                    </li>
                  </ComboboxOption>
                </ComboboxOptions>
              </div>
            </Combobox>
            <div class="flex items-center gap-3 pt-0.5 text-xs">
              <span class="text-fg-muted dark:text-fg-muted-dark">Practice page:</span>
              <button type="button" @click="copyPracticeLink(s)" class="text-blue-600 dark:text-blue-400 hover:underline">
                {{ practiceLinkCopied === s.id ? 'Copied!' : 'Copy link' }}
              </button>
              <button type="button" @click="resetPracticeLink(s)" class="text-fg-muted dark:text-fg-muted-dark hover:underline">Reset link</button>
            </div>
          </div>
        </div>
        <div class="flex gap-2">
          <button @click="savePanel" :disabled="panelSaving"
            class="px-3 py-1 rounded-md bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-xs font-medium">{{ panelSaving ? 'Saving…' : 'Save' }}</button>
          <button @click="cancelEditDetails" class="px-3 py-1 rounded-md border border-stroke-strong dark:border-stroke-strong-dark text-xs text-fg-secondary dark:text-fg-secondary-dark">Cancel</button>
        </div>
      </div>

      <!-- Students and address (right padding keeps clear of the floating buttons) -->
      <div v-if="(headerStudents.length && !editingDetails) || contact.address" class="space-y-1 pr-28">
        <div v-if="headerStudents.length && !editingDetails" class="flex flex-wrap gap-x-3 gap-y-1">
          <span v-for="s in headerStudents" :key="s.id ?? s.name" class="flex items-center gap-1.5 text-fg-secondary dark:text-fg-secondary-dark text-sm">
            <span
              class="h-5 w-5 rounded-full flex-shrink-0 flex items-center justify-center text-white text-[10px] font-semibold"
              :style="{ background: avatarGradient(s.name) }"
            >{{ initials(s.name) || '?' }}</span>
            {{ s.name }}
          </span>
        </div>
        <p v-if="contact.address" class="flex items-center gap-1.5 text-sm text-fg-secondary dark:text-fg-secondary-dark">
          <MapPinIcon class="h-3.5 w-3.5 flex-shrink-0 text-fg-faint dark:text-fg-faint-dark" />
          {{ contact.address }}
        </p>
      </div>

      <!-- Send Text / Add Note (note, outside text/email) / Log Call / Links — floated bottom-right, takes no vertical space -->
      <div v-if="!editingDetails" class="absolute bottom-2 right-2 flex items-center">
        <RouterLink v-if="canText" :to="{ name: 'AdminSmsInbox', query: { phone: contact.phone } }" title="Send a text"
          :class="HEADER_ICON_BTN">
          <ChatBubbleLeftEllipsisIcon class="h-4 w-4" />
        </RouterLink>
        <LogTouchButtons @log="startLog" :button-class="HEADER_ICON_BTN" />
        <ActionMenuButton :icon="LinkIcon" title="Copy a link" down :button-class="HEADER_ICON_BTN"
          :sections="headerLinksMenu" :busy-key="linkBusy" :done="linkDone === 'links'" />
      </div>
    </div>

    <!-- Alternate contacts: sub-cards right under the primary's card, no section title -->
    <div v-if="!recipientsLoading" class="-mt-1 ml-2 sm:ml-3 space-y-2">
      <div v-if="recipients.length || addingRecipient" class="space-y-2">
      <AlternateContactCard
        v-for="r in recipients"
        :key="r.id"
        :recipient="r"
        :contact-id="contact.id"
        :can-email="canEmail"
        :icon-btn="HEADER_ICON_BTN"
        @updated="data => recipients = recipients.map(x => x.id === data.id ? data : x)"
        @deleted="id => recipients = recipients.filter(x => x.id !== id)"
        @log="entry => startLog({ ...entry, recipient: r })"
        @primary-changed="onPrimaryChanged"
      />
      <div v-if="addingRecipient" class="rounded-xl border border-blue-400/60 dark:border-blue-500/40 bg-contact-card-sub-bg dark:bg-contact-card-sub-bg-dark p-3 space-y-2">
        <p class="text-xs font-medium text-fg-muted dark:text-fg-muted-dark">New alternate contact</p>
        <input v-for="f in NEW_RECIPIENT_FIELDS" :key="f.key" v-model="newRecipient[f.key]" :type="f.type" :placeholder="f.placeholder"
          autocomplete="off" @keydown.enter="submitNewRecipient()" @keydown.esc="addingRecipient = false"
          class="w-full px-2.5 py-1.5 rounded border border-stroke-strong dark:border-stroke-strong-dark bg-surface dark:bg-surface-muted-dark text-fg-base dark:text-fg-base-dark text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
        <label class="flex items-center gap-2 text-sm text-fg-secondary dark:text-fg-secondary-dark" :title="FULL_ACCESS_HINT">
          <input v-model="newRecipient.billing_access" type="checkbox" class="rounded" /> Full account access
        </label>
        <div class="flex gap-2">
          <button @click="submitNewRecipient()" :disabled="!newRecipient.name.trim()"
            class="px-3 py-1 rounded-md bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-xs font-medium">Add</button>
          <button @click="addingRecipient = false"
            class="px-3 py-1 rounded-md border border-stroke-strong dark:border-stroke-strong-dark text-xs text-fg-secondary dark:text-fg-secondary-dark">Cancel</button>
        </div>
      </div>
      </div>
      <button v-if="!addingRecipient" @click="startAddRecipient()" class="text-xs text-blue-600 dark:text-blue-400 hover:underline">+ Add alternate contact</button>
    </div>

    <!-- Inquiries: this contact's open signup cards (same card as the dashboard) -->
    <div v-if="activeInquiries.length" class="pt-1">
      <button @click="inquiriesOpen = !inquiriesOpen" class="w-full flex items-center gap-1 text-xs font-medium text-fg-muted dark:text-fg-muted-dark uppercase tracking-wider" :class="{ 'mb-2': inquiriesOpen }">
        <ChevronRightIcon class="h-3 w-3 transition-transform" :class="{ 'rotate-90': inquiriesOpen }" />
        Inquiries
        <span class="ml-auto normal-case tracking-normal font-normal text-fg-faint dark:text-fg-faint-dark">{{ activeInquiries.length }} open</span>
      </button>
      <div v-if="inquiriesOpen" class="space-y-2">
        <SignupCard
          v-for="inq in activeInquiries"
          :key="inq.id"
          :inquiry="inq"
          is-admin
          @updated="replaceInquiry"
        />
      </div>
    </div>

    <!-- Activity: tiered timeline (signup history + forms + email) -->
    <div class="pt-1" :class="activeInquiries.length && 'border-t border-stroke dark:border-stroke-dark'">
      <button @click="activityOpen = !activityOpen" class="w-full flex items-center gap-1 text-xs font-medium text-fg-muted dark:text-fg-muted-dark uppercase tracking-wider" :class="{ 'mb-2': activityOpen }">
        <ChevronRightIcon class="h-3 w-3 transition-transform" :class="{ 'rotate-90': activityOpen }" />
        Activity
        <span class="ml-auto normal-case tracking-normal font-normal text-fg-faint dark:text-fg-faint-dark">
          {{ panelActivityLoading ? '…' : `${panelActivity?.timeline?.length || 0} events` }}
        </span>
      </button>
      <template v-if="activityOpen">
        <p v-if="panelActivity?.next_follow_up" :class="['text-xs mb-2', followUpOverdue ? 'text-red-600 dark:text-red-400 font-medium' : 'text-fg-secondary dark:text-fg-secondary-dark']">
          Next follow-up: {{ fmtDate(panelActivity.next_follow_up) }}<span v-if="followUpOverdue"> · overdue</span>
        </p>
        <ActivityTimeline title="" :events="panelActivity?.timeline || []" :loading="panelActivityLoading" />
      </template>
    </div>

    <!-- Lesson Reminders -->
    <div v-if="panelActivity?.lesson_series?.length" class="pt-1 border-t border-stroke dark:border-stroke-dark">
      <button @click="remindersOpen = !remindersOpen" class="w-full flex items-center gap-1 text-xs font-medium text-fg-muted dark:text-fg-muted-dark uppercase tracking-wider" :class="{ 'mb-2': remindersOpen }">
        <ChevronRightIcon class="h-3 w-3 transition-transform" :class="{ 'rotate-90': remindersOpen }" />
        Lesson Reminders
        <span class="ml-auto normal-case tracking-normal font-normal text-fg-faint dark:text-fg-faint-dark">{{ panelActivity.lesson_series.length }} series</span>
      </button>
      <div v-if="remindersOpen" class="space-y-2">
        <div
          v-for="series in panelActivity.lesson_series"
          :key="series.id"
          class="text-xs pb-2 border-b border-stroke dark:border-stroke-dark last:border-0 last:pb-0"
        >
          <p class="font-medium text-fg-base dark:text-fg-base-dark">{{ series.instrument || 'Lesson' }} with {{ series.teacher_name || '—' }}</p>
          <p class="text-fg-muted dark:text-fg-muted-dark mt-0.5">{{ series.schedule_label }}<span v-if="series.start_time"> · {{ series.start_time }}</span></p>
          <div class="flex items-center gap-3 mt-1">
            <label class="flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" :checked="series.email_reminders_enabled"
                @change="toggleSeriesReminder(series, 'email_reminders_enabled')"
                class="rounded border-stroke-strong dark:border-stroke-strong-dark" />
              <span class="text-fg-secondary dark:text-fg-secondary-dark">Email</span>
            </label>
            <label class="flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" :checked="series.sms_reminders_enabled"
                @change="toggleSeriesReminder(series, 'sms_reminders_enabled')"
                class="rounded border-stroke-strong dark:border-stroke-strong-dark" />
              <span class="text-fg-secondary dark:text-fg-secondary-dark">Text</span>
            </label>
          </div>
        </div>
      </div>
    </div>

    <!-- Upcoming Lessons: everything on the calendar after today (series are materialized 160 days out) -->
    <div class="pt-1 border-t border-stroke dark:border-stroke-dark">
      <button @click="upcomingOpen = !upcomingOpen" class="w-full flex items-center gap-1 text-xs font-medium text-fg-muted dark:text-fg-muted-dark uppercase tracking-wider" :class="{ 'mb-2': upcomingOpen }">
        <ChevronRightIcon class="h-3 w-3 transition-transform" :class="{ 'rotate-90': upcomingOpen }" />
        Upcoming Lessons
        <span class="ml-auto normal-case tracking-normal font-normal text-fg-faint dark:text-fg-faint-dark">
          {{ panelActivityLoading ? '…' : upcomingLessons.length ? `${upcomingLessons.length} through ${fmtShortDate(upcomingLessons.at(-1).lesson_date)}` : 'none' }}
        </span>
      </button>
      <template v-if="upcomingOpen">
        <div v-if="panelActivityLoading" class="text-xs text-fg-faint dark:text-fg-faint-dark">Loading…</div>
        <div v-else-if="!upcomingLessons.length" class="text-xs text-fg-faint dark:text-fg-faint-dark italic">Nothing scheduled</div>
        <div v-else class="space-y-1.5">
          <div
            v-for="l in (showAllUpcoming ? upcomingLessons : upcomingLessons.slice(0, 8))"
            :key="l.id"
            class="text-xs flex items-start justify-between gap-1"
          >
            <div>
              <span class="font-medium text-fg-base dark:text-fg-base-dark">{{ fmtShortDate(l.lesson_date) }}</span>
              <span class="text-fg-muted dark:text-fg-muted-dark"> · {{ l.lesson_time }} · {{ l.teacher_name }} · {{ l.duration_minutes }}min</span>
              <p v-if="l.students.length" class="text-fg-secondary dark:text-fg-secondary-dark">{{ l.students.join(', ') }}</p>
            </div>
            <span v-if="l.billing_status && l.billing_status !== 'scheduled'" :class="billingStatusClass(l.billing_status)" class="text-xs px-1.5 py-0.5 rounded font-medium flex-shrink-0">{{ l.billing_status }}</span>
          </div>
          <button
            v-if="upcomingLessons.length > 8"
            @click="showAllUpcoming = !showAllUpcoming"
            class="text-xs text-blue-600 dark:text-blue-400 hover:underline"
          >{{ showAllUpcoming ? 'Show fewer' : `Show all ${upcomingLessons.length}` }}</button>
        </div>
      </template>
    </div>

    <!-- Recent Lessons -->
    <div class="pt-1 border-t border-stroke dark:border-stroke-dark">
      <button @click="lessonsOpen = !lessonsOpen" class="w-full flex items-center gap-1 text-xs font-medium text-fg-muted dark:text-fg-muted-dark uppercase tracking-wider" :class="{ 'mb-2': lessonsOpen }">
        <ChevronRightIcon class="h-3 w-3 transition-transform" :class="{ 'rotate-90': lessonsOpen }" />
        Recent Lessons
        <span class="ml-auto normal-case tracking-normal font-normal text-fg-faint dark:text-fg-faint-dark">
          {{ panelActivityLoading ? '…' : `${panelActivity?.lessons?.length || 0} in last 30 days` }}
        </span>
      </button>
      <template v-if="lessonsOpen">
        <p v-for="u in panelActivity?.absence_courtesy_used || []" :key="`used-${u.student_id}`" class="text-xs text-fg-muted dark:text-fg-muted-dark mb-1">
          Absence courtesy used: {{ u.student_name }} · {{ fmtShortDate(u.lesson_date) }} ({{ fmtCents(u.amount_cents) }}{{ u.source === 'admin' ? ', granted' : '' }})
        </p>
        <div v-if="panelActivityLoading" class="text-xs text-fg-faint dark:text-fg-faint-dark">Loading…</div>
        <div v-else-if="!panelActivity?.lessons?.length" class="text-xs text-fg-faint dark:text-fg-faint-dark italic">No lessons in the last 30 days</div>
        <div v-else class="space-y-2">
          <div
            v-for="l in panelActivity.lessons"
            :key="l.id"
            class="text-xs pb-2 border-b border-stroke dark:border-stroke-dark last:border-0 last:pb-0"
          >
            <div class="flex items-start justify-between gap-1">
              <span class="font-medium text-fg-base dark:text-fg-base-dark">{{ fmtShortDate(l.lesson_date) }}</span>
              <span v-if="l.billing_status" :class="billingStatusClass(l.billing_status)" class="text-xs px-1.5 py-0.5 rounded font-medium">{{ l.billing_status }}</span>
              <span v-else class="text-fg-faint dark:text-fg-faint-dark">—</span>
            </div>
            <p class="text-fg-muted dark:text-fg-muted-dark mt-0.5">{{ l.lesson_time }} · {{ l.teacher_name }} · {{ l.duration_minutes }}min</p>
            <p v-if="l.price_cents != null" class="text-fg-secondary dark:text-fg-secondary-dark mt-0.5">{{ fmtCents(l.price_cents) }}</p>
            <!-- Attendance -->
            <div v-if="l.attendance?.length" class="flex flex-wrap gap-1 mt-1">
              <span
                v-for="a in l.attendance"
                :key="a.name"
                :class="attendanceClass(a.status)"
                class="px-1.5 py-0.5 rounded text-xs"
              >{{ a.name }}: {{ a.status || '—' }}</span>
              <AbsenceCourtesyBadge
                v-for="c in (l.absence_courtesies || []).filter(c => c.status === 'absent')"
                :key="`courtesy-${c.student_id}`"
                :lesson-id="l.id" :info="c"
                @updated="l.absence_courtesies = $event"
              />
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- Financial Details -->
    <div class="pt-1 border-t border-stroke dark:border-stroke-dark">
      <button @click="financialOpen = !financialOpen" class="w-full flex items-center gap-1 text-xs font-medium text-fg-muted dark:text-fg-muted-dark uppercase tracking-wider" :class="{ 'mb-2': financialOpen }">
        <ChevronRightIcon class="h-3 w-3 transition-transform" :class="{ 'rotate-90': financialOpen }" />
        Financial Details
        <span class="ml-auto normal-case tracking-normal font-normal" :class="contact.balance_cents > 0 ? 'text-red-500 dark:text-red-400' : 'text-fg-faint dark:text-fg-faint-dark'">{{ fmtCents(contact.balance_cents) }}</span>
      </button>
      <div v-if="financialOpen" class="space-y-2">
        <div>
          <p class="text-xs font-medium text-fg-muted dark:text-fg-muted-dark uppercase tracking-wider mb-0.5">Payment Method</p>
          <p v-if="contact.stripe_pm_last4" class="text-fg-secondary dark:text-fg-secondary-dark text-sm">{{ fmtPaymentMethod(contact) }}<span v-if="contact.stripe_pm_added_by" class="text-fg-muted dark:text-fg-muted-dark"> · added by {{ contact.stripe_pm_added_by }}</span></p>
          <p v-else class="text-fg-faint dark:text-fg-faint-dark text-sm">—</p>
        </div>
        <div>
          <p class="text-xs font-medium text-fg-muted dark:text-fg-muted-dark uppercase tracking-wider mb-0.5">Balance</p>
          <p :class="contact.balance_cents > 0 ? 'text-red-500 dark:text-red-400 text-sm' : 'text-fg-secondary dark:text-fg-secondary-dark text-sm'">
            {{ fmtCents(contact.balance_cents) }}
          </p>
        </div>
        <!-- Hardship offer status -->
        <div v-if="hardshipOffer">
          <p class="text-xs font-medium text-fg-muted dark:text-fg-muted-dark uppercase tracking-wider mb-0.5">Hardship Offer</p>
          <p class="text-sm" :class="{
            'text-amber-500': hardshipOffer.status === 'offered',
            'text-blue-400':  hardshipOffer.status === 'active' || hardshipOffer.status === 'accepted',
            'text-green-400': hardshipOffer.status === 'completed',
            'text-fg-muted dark:text-fg-muted-dark': hardshipOffer.status === 'expired',
          }">
            <template v-if="hardshipOffer.status === 'offered'">Offer sent — awaiting response</template>
            <template v-else-if="hardshipOffer.status === 'accepted'">Accepted — first payment processing</template>
            <template v-else-if="hardshipOffer.status === 'active'">Active — {{ hardshipOffer.consecutive_payments }}/{{ hardshipOffer.forgiveness_cliff_months }} payments</template>
            <template v-else-if="hardshipOffer.status === 'completed'">Completed — balance cleared ✓</template>
            <template v-else-if="hardshipOffer.status === 'expired'">Expired</template>
            <template v-else>{{ hardshipOffer.status }}</template>
          </p>
        </div>
        <!-- Lesson package credit -->
        <div v-if="packageCredit">
          <p class="text-xs font-medium text-fg-muted dark:text-fg-muted-dark uppercase tracking-wider mb-0.5">Package Credit</p>
          <p class="text-sm text-blue-500 dark:text-blue-400">
            {{ fmtCents(packageCredit.remaining_cents) }} remaining<template v-if="packageCredit.nearest_expires_at"> (expires {{ fmtDate(packageCredit.nearest_expires_at) }})</template>
          </p>
        </div>
        <!-- Discounts (admin + signups; same ledger entries as Financial → Discounts) -->
        <div v-if="discounts">
          <div class="flex items-center">
            <p class="text-xs font-medium text-fg-muted dark:text-fg-muted-dark uppercase tracking-wider mb-0.5">Discounts</p>
            <button v-if="!discountFormOpen" @click="openDiscountForm" class="ml-auto text-xs text-blue-600 dark:text-blue-400 hover:underline">+ Add discount</button>
          </div>
          <p v-for="d in discounts" :key="d.id" class="text-sm text-fg-secondary dark:text-fg-secondary-dark flex items-center gap-2">
            <span class="text-green-600 dark:text-green-400 font-mono">−{{ fmtCents(d.amount_cents) }}</span>
            <span class="text-xs text-fg-muted dark:text-fg-muted-dark truncate">
              {{ d.lesson_date ? `lesson ${fmtDate(d.lesson_date)}` : 'next bill' }}<template v-if="d.reason"> · {{ d.reason }}</template>
            </span>
            <button class="ml-auto text-xs text-red-500 dark:text-red-400 hover:underline" @click="cancelDiscount(d)">Cancel</button>
          </p>
          <p v-if="!discounts.length && !discountFormOpen" class="text-fg-faint dark:text-fg-faint-dark text-sm">None pending</p>
          <div v-if="discountFormOpen" class="mt-1 space-y-1.5">
            <select v-model="discountForm.lessonId" @change="onDiscountLessonSelect"
              class="w-full px-2 py-1 text-sm rounded border border-stroke dark:border-stroke-dark bg-transparent text-fg-base dark:text-fg-base-dark">
              <option :value="null">Next bill (any lesson)</option>
              <option v-for="l in discountLessons" :key="l.id" :value="l.id">
                {{ fmtDate(l.lesson_date) }}{{ l.lesson_time ? ' ' + l.lesson_time : '' }} — {{ l.attendance }}{{ l.price_cents != null ? ' · ' + fmtCents(l.price_cents) : '' }}
              </option>
            </select>
            <div class="flex items-center gap-2">
              <span class="text-sm text-fg-muted dark:text-fg-muted-dark">$</span>
              <input v-model="discountForm.amount" type="number" min="0.01" step="0.01" placeholder="Amount"
                class="w-24 px-2 py-1 text-sm rounded border border-stroke dark:border-stroke-dark bg-transparent text-fg-base dark:text-fg-base-dark" />
              <input v-model="discountForm.reason" type="text" placeholder="Reason (optional)"
                class="flex-1 min-w-0 px-2 py-1 text-sm rounded border border-stroke dark:border-stroke-dark bg-transparent text-fg-base dark:text-fg-base-dark" />
            </div>
            <div class="flex items-center gap-2">
              <button :disabled="discountSaving || !discountForm.amount" @click="saveDiscount"
                class="px-2 py-1 text-xs rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">{{ discountSaving ? 'Saving…' : 'Save discount' }}</button>
              <button @click="discountFormOpen = false" class="text-xs text-fg-muted dark:text-fg-muted-dark hover:underline">Cancel</button>
            </div>
          </div>
          <p v-if="discountError" class="text-xs text-red-500 dark:text-red-400 mt-1">{{ discountError }}</p>
        </div>
        <!-- Family's own rates (admin + signups): assisted membership rate + package rate.
             A prepay-only family's rate is edited in Prepay Only (admin). -->
        <div v-if="pricing">
          <p class="text-xs font-medium text-fg-muted dark:text-fg-muted-dark uppercase tracking-wider mb-0.5">Pricing</p>
          <label class="flex items-center gap-2 text-sm text-fg-secondary dark:text-fg-secondary-dark">
            <input type="checkbox" :checked="pricing.assisted" :disabled="pricingSaving" @change="savePricing({ assisted: $event.target.checked })">
            Assisted rate
            <span class="text-xs text-fg-faint dark:text-fg-faint-dark">
              membership {{ fmtCents(pricing.assisted_rates[0]) }}/mo · {{ fmtCents(pricing.assisted_rates[1]) }} sibling
            </span>
          </label>
          <p class="text-xs text-fg-muted dark:text-fg-muted-dark mt-0.5">
            <template v-if="pricing.assisted_override === null">
              {{ pricing.assisted_requested ? 'On because they asked for financial aid' : 'Off — turns on automatically if they ask for financial aid' }}
            </template>
            <template v-else>
              Set by staff<template v-if="pricing.assisted_requested !== pricing.assisted"> ({{ pricing.assisted_requested ? 'they asked for aid' : 'they never asked for aid' }})</template> ·
              <button class="text-blue-600 dark:text-blue-400 hover:underline" :disabled="pricingSaving" @click="savePricing({ assisted: null })">Reset to automatic</button>
            </template>
          </p>
          <div v-if="!pricing.prepay" class="flex items-center gap-2 mt-1.5">
            <span class="text-sm text-fg-muted dark:text-fg-muted-dark">$</span>
            <input v-model.number="packageRate" type="number" min="1" step="0.01" placeholder="Package rate"
              class="w-28 px-2 py-1 text-sm rounded border border-stroke dark:border-stroke-dark bg-transparent text-fg-base dark:text-fg-base-dark" />
            <span class="text-xs text-fg-faint dark:text-fg-faint-dark">/lesson (4+)</span>
            <button :disabled="pricingSaving || !packageRate || Math.round(packageRate * 100) === pricing.package_rate_cents"
              @click="savePricing({ package_rate_cents: Math.round(packageRate * 100) })"
              class="ml-auto px-2 py-1 text-xs rounded border border-stroke dark:border-stroke-dark text-fg-secondary dark:text-fg-secondary-dark disabled:opacity-50">Save rate</button>
            <button v-if="pricing.package_rate_cents" :disabled="pricingSaving" @click="savePricing({ package_rate_cents: null })"
              class="text-xs text-red-500 dark:text-red-400 hover:underline">Clear</button>
          </div>
          <p v-if="!pricing.prepay" class="text-xs text-fg-muted dark:text-fg-muted-dark mt-0.5">
            {{ pricing.package_rate_cents
              ? 'Their package page sells 1–10 lessons at this rate (+$3/$2/$1 per lesson for 1/2/3) instead of the standard packages.'
              : 'Standard packages. Set a rate to sell them 1–10 lessons at their own price.' }}
          </p>
          <p v-if="pricingError" class="text-xs text-red-500 dark:text-red-400 mt-1">{{ pricingError }}</p>
        </div>
        <!-- Prepay-only (billing/prepay.py) — offered once a lesson is 14+ days unpaid -->
        <div v-if="prepay && (prepay.enabled || prepay.eligible)">
          <p class="text-xs font-medium text-fg-muted dark:text-fg-muted-dark uppercase tracking-wider mb-0.5">Prepay Only</p>
          <template v-if="prepay.enabled">
            <p class="text-sm text-amber-500">
              On since {{ fmtUtcDate(prepay.required_at) }} · lessons from {{ fmtDate(prepay.effective_date) }} must be prepaid
            </p>
            <p v-if="prepay.status" class="text-xs text-fg-muted dark:text-fg-muted-dark mt-0.5">
              <template v-if="prepay.status.covered_through">Paid through {{ fmtDate(prepay.status.covered_through) }}</template>
              <template v-else>No lessons prepaid</template>
              <template v-if="prepay.status.deadline"> · buy by {{ fmtDate(prepay.status.deadline) }} to keep {{ fmtDate(prepay.status.next_uncovered) }}</template>
              <template v-if="prepay.status.balance_cents > 0"> · <span class="text-red-500 dark:text-red-400">{{ fmtCents(prepay.status.balance_cents) }} owed</span></template>
            </p>
          </template>
          <p v-else class="text-xs text-fg-muted dark:text-fg-muted-dark">
            Unpaid since {{ fmtDate(prepay.oldest_overdue) }}. Prepay-only starts 10 days after you turn it on.
          </p>
          <div class="flex items-center gap-2 mt-1">
            <span class="text-sm text-fg-muted dark:text-fg-muted-dark">$</span>
            <input v-model.number="prepayPrice" type="number" min="1" step="0.01" placeholder="4+ lesson rate"
              class="w-28 px-2 py-1 text-sm rounded border border-stroke dark:border-stroke-dark bg-transparent text-fg-base dark:text-fg-base-dark" />
            <span class="text-xs text-fg-faint dark:text-fg-faint-dark">/lesson (4+)</span>
            <button v-if="!prepay.enabled" :disabled="prepaySaving || !prepayPrice" @click="savePrepay(true)"
              class="ml-auto px-2 py-1 text-xs rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50">Turn on</button>
            <button v-else :disabled="prepaySaving || !prepayPrice || Math.round(prepayPrice * 100) === prepay.lesson_price_cents" @click="savePrepay(true)"
              class="ml-auto px-2 py-1 text-xs rounded border border-stroke dark:border-stroke-dark text-fg-secondary dark:text-fg-secondary-dark disabled:opacity-50">Save rate</button>
          </div>
          <div v-if="prepay.enabled" class="flex items-center gap-3 mt-1">
            <button class="text-xs text-blue-600 dark:text-blue-400 hover:underline" @click="copyPrepayLink">{{ prepayCopied ? 'Copied ✓' : 'Copy purchase link' }}</button>
            <button class="text-xs text-red-500 dark:text-red-400 hover:underline" :disabled="prepaySaving" @click="savePrepay(false)">Turn off</button>
          </div>
          <p v-if="prepayError" class="text-xs text-red-500 dark:text-red-400 mt-1">{{ prepayError }}</p>
        </div>
        <!-- Settlement offer (collections.py) — $1/minute payoff of the overdue lessons, link for a staff email -->
        <div v-if="settlement?.case && (settlement.open || settlement.available || settlement.offered_at)">
          <p class="text-xs font-medium text-fg-muted dark:text-fg-muted-dark uppercase tracking-wider mb-0.5">Settlement Offer</p>
          <template v-if="settlement.open || settlement.available">
            <p :class="['text-sm', settlement.open ? 'text-amber-500' : 'text-fg-base dark:text-fg-base-dark']">
              {{ fmtCents(settlement.full_cents) }} → {{ fmtCents(settlement.settle_cents) }} for {{ settlement.lessons }} overdue lesson{{ settlement.lessons === 1 ? '' : 's' }}
              · {{ settlement.open ? 'open' : 'would be open' }} until {{ fmtDate(settlement.until) }}
            </p>
            <p v-if="settlement.credit_cents > 0" class="text-xs text-fg-muted dark:text-fg-muted-dark mt-0.5">
              Their {{ fmtCents(settlement.credit_cents) }} account credit also comes off — Pay Balance charges {{ fmtCents(Math.max(0, settlement.settle_cents - settlement.credit_cents)) }}
            </p>
            <div class="flex items-center gap-3 mt-1">
              <button v-if="settlement.open" class="text-xs text-blue-600 dark:text-blue-400 hover:underline" @click="copySettlementLink">{{ settlementCopied ? 'Copied ✓' : 'Copy payment link' }}</button>
              <button v-else :disabled="settlementSaving" @click="createSettlement"
                class="px-2 py-1 text-xs rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50">Create offer</button>
            </div>
          </template>
          <p v-else class="text-xs text-fg-muted dark:text-fg-muted-dark">Offered {{ fmtUtcDate(settlement.offered_at) }} and expired — one offer per case.</p>
          <p v-if="settlementError" class="text-xs text-red-500 dark:text-red-400 mt-1">{{ settlementError }}</p>
        </div>
        <div v-if="contact.stripe_customer_id || panelActivity?.linked_stripe_customers?.length">
          <p class="text-xs font-medium text-fg-muted dark:text-fg-muted-dark uppercase tracking-wider mb-0.5">
            {{ panelActivity?.linked_stripe_customers?.length ? 'Stripe Customers' : 'Stripe Customer' }}
          </p>
          <a
            v-if="contact.stripe_customer_id"
            :href="`https://dashboard.stripe.com/customers/${contact.stripe_customer_id}`"
            target="_blank" rel="noopener"
            class="font-mono text-xs text-blue-600 dark:text-blue-400 hover:underline break-all"
          >{{ contact.stripe_customer_id }} ↗</a>
          <span v-if="contact.stripe_customer_id && panelActivity?.linked_stripe_customers?.length" class="text-xs text-fg-faint dark:text-fg-faint-dark"> · primary</span>
          <div v-for="c in panelActivity?.linked_stripe_customers || []" :key="c.id" class="mt-0.5">
            <a
              :href="`https://dashboard.stripe.com/customers/${c.id}`"
              target="_blank" rel="noopener"
              class="font-mono text-xs text-blue-600 dark:text-blue-400 hover:underline break-all"
            >{{ c.id }} ↗</a>
            <p v-if="c.label" class="text-xs text-fg-faint dark:text-fg-faint-dark">{{ c.label }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Recent Charges -->
    <div class="pt-1 border-t border-stroke dark:border-stroke-dark">
      <button @click="chargesOpen = !chargesOpen" class="w-full flex items-center gap-1 text-xs font-medium text-fg-muted dark:text-fg-muted-dark uppercase tracking-wider" :class="{ 'mb-2': chargesOpen }">
        <ChevronRightIcon class="h-3 w-3 transition-transform" :class="{ 'rotate-90': chargesOpen }" />
        Recent Charges
        <span class="ml-auto normal-case tracking-normal font-normal text-fg-faint dark:text-fg-faint-dark">
          {{ panelActivityLoading ? '…' : `${panelActivity?.charges?.length || 0} in last 30 days` }}
        </span>
      </button>
      <template v-if="chargesOpen">
        <div v-if="panelActivityLoading" class="text-xs text-fg-faint dark:text-fg-faint-dark">Loading…</div>
        <div v-else-if="!contact.stripe_customer_id" class="text-xs text-fg-faint dark:text-fg-faint-dark italic">No Stripe customer</div>
        <div v-else-if="!panelActivity?.charges?.length" class="text-xs text-fg-faint dark:text-fg-faint-dark italic">No charges in the last 30 days</div>
        <div v-else class="space-y-1.5">
          <div
            v-for="ch in panelActivity.charges"
            :key="ch.id"
            class="flex items-start justify-between gap-2 text-xs pb-1.5 border-b border-stroke dark:border-stroke-dark last:border-0 last:pb-0"
          >
            <div class="min-w-0">
              <div class="flex items-baseline gap-1.5">
                <span class="font-medium text-fg-base dark:text-fg-base-dark">{{ fmtCents(ch.amount_cents) }}</span>
                <span class="text-fg-faint dark:text-fg-faint-dark">{{ fmtUnixDate(ch.created) }}</span>
                <span :class="chargeStatusClass(ch)">{{ chargeStatusLabel(ch) }}</span>
                <a
                  :href="`https://dashboard.stripe.com/charges/${ch.id}`"
                  target="_blank" rel="noopener"
                  class="text-fg-faint dark:text-fg-faint-dark hover:text-blue-600 dark:hover:text-blue-400"
                >↗</a>
              </div>
              <p v-if="ch.lesson_dates?.length" class="text-fg-muted dark:text-fg-muted-dark mt-0.5">
                {{ fmtLessonDateList(ch.lesson_dates) }}
              </p>
            </div>
          </div>
        </div>
      </template>
    </div>

    <template #actions>
      <div class="flex items-center gap-1.5">
        <button
          @click="copyAccountLink()"
          :disabled="linkBusy === 'copy-account'"
          :title="linkBusy === 'copy-account' ? 'Generating…' : 'Copy Account Link'"
          class="h-8 w-8 flex items-center justify-center rounded-md border transition-colors disabled:opacity-40"
          :class="linkDone === 'copy-account' ? DONE_BTN : IDLE_BTN"
        >
          <component :is="linkDone === 'copy-account' ? CheckIcon : IdentificationIcon" class="h-4 w-4 flex-shrink-0" />
        </button>

        <button
          v-if="!contact.sms_consent"
          @click="copySmsOptInLink()"
          :disabled="linkBusy === 'copy-optin'"
          :title="linkBusy === 'copy-optin' ? 'Generating…' : 'Copy SMS Opt-In Link'"
          class="h-8 w-8 flex items-center justify-center rounded-md border transition-colors disabled:opacity-40"
          :class="linkDone === 'copy-optin' ? DONE_BTN : IDLE_BTN"
        >
          <component :is="linkDone === 'copy-optin' ? CheckIcon : ChatBubbleLeftRightIcon" class="h-4 w-4 flex-shrink-0" />
        </button>

        <ActionMenuButton :icon="LinkIcon" title="Copy Other Links" :sections="copyMenu"
          :busy-key="linkBusy" :done="linkDone === 'copy'" />

        <ActionMenuButton :icon="EnvelopeIcon" :title="contact.email ? 'Email a Link' : 'No email on file'"
          :disabled="!contact.email" :sections="emailMenu" :busy-key="linkBusy" :done="linkDone === 'email'" />

        <ActionMenuButton v-if="contact.sms_consent && contact.phone" :icon="ChatBubbleLeftIcon" title="Text a Link"
          :sections="textMenu" :busy-key="linkBusy" :done="linkDone === 'text'" />
      </div>

    </template>
  </SidePanel>

  <Teleport to="body">
    <NotePromptModal
      v-if="logPrompt"
      :title="logPromptTitle"
      :subtitle="logPromptSubtitle"
      :saving="logPromptSaving"
      :note-required="logPrompt.kind === 'note'"
      :show-timestamp="logPrompt.kind !== 'note'"
      :save-label="logPrompt.kind === 'note' ? 'Save Note' : 'Log'"
      @save="saveLogPrompt"
      @close="logPrompt = null"
    />
  </Teleport>

  <InvoiceModal v-if="showInvoiceModal" :contact="contact" @close="showInvoiceModal = false" />

  <!-- Custom Package Modal -->
  <Teleport to="body">
    <div v-if="customPkg" class="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-10 px-4">
      <div class="fixed inset-0 bg-black/60" @click="customPkg = null" />
      <div class="relative bg-surface dark:bg-surface-dark rounded-xl shadow-2xl w-full max-w-sm">
        <div class="px-6 pt-6 pb-4 border-b border-stroke dark:border-stroke-dark">
          <h2 class="text-lg font-semibold text-fg-base dark:text-fg-base-dark">Custom Package</h2>
          <p class="text-sm text-fg-muted dark:text-fg-muted-dark mt-0.5">{{ contact.name }}</p>
        </div>

        <!-- Step 1: count + price -->
        <form v-if="!customPkg.link" @submit.prevent="createCustomPackage" class="px-6 py-5 space-y-4">
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-medium text-fg-muted dark:text-fg-muted-dark mb-1">Lessons</label>
              <input v-model.number="customPkg.size" type="number" min="1" step="1" required
                class="w-full px-3 py-2 text-sm rounded-lg border border-stroke dark:border-stroke-dark bg-surface dark:bg-surface-dark text-fg-base dark:text-fg-base-dark" />
            </div>
            <div>
              <label class="block text-xs font-medium text-fg-muted dark:text-fg-muted-dark mb-1">Total price ($)</label>
              <input v-model.number="customPkg.price" type="number" min="0.01" step="0.01" required
                class="w-full px-3 py-2 text-sm rounded-lg border border-stroke dark:border-stroke-dark bg-surface dark:bg-surface-dark text-fg-base dark:text-fg-base-dark" />
            </div>
          </div>
          <p v-if="customPkg.size > 0 && customPkg.price > 0" class="text-xs text-fg-faint dark:text-fg-faint-dark -mt-2">
            ${{ (customPkg.price / customPkg.size).toFixed(2) }}/lesson
          </p>
          <div>
            <label class="block text-xs font-medium text-fg-muted dark:text-fg-muted-dark mb-1">Link expires in</label>
            <select v-model.number="customPkg.hours"
              class="w-full px-3 py-2 text-sm rounded-lg border border-stroke dark:border-stroke-dark bg-surface dark:bg-surface-dark text-fg-base dark:text-fg-base-dark">
              <option v-for="opt in PACKAGE_LINK_DURATIONS" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
            </select>
          </div>
          <p v-if="customPkg.error" class="text-red-500 dark:text-red-400 text-sm">{{ customPkg.error }}</p>
          <div class="flex gap-3 pt-2">
            <button type="button" @click="customPkg = null"
              class="flex-1 px-4 py-2 text-sm border border-stroke dark:border-stroke-dark rounded-lg text-fg-secondary dark:text-fg-secondary-dark hover:bg-surface-muted dark:hover:bg-surface-muted-dark transition-colors">
              Cancel
            </button>
            <button type="submit" :disabled="customPkg.busy"
              class="flex-1 px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium disabled:opacity-50 transition-colors">
              {{ customPkg.busy ? 'Creating…' : 'Create Link' }}
            </button>
          </div>
        </form>

        <!-- Step 2: send it -->
        <div v-else class="px-6 py-5 space-y-4">
          <div class="bg-surface-muted dark:bg-surface-muted-dark rounded-lg px-4 py-3 text-sm">
            <p class="text-fg-base dark:text-fg-base-dark font-medium">{{ customPkg.size }} lessons · ${{ customPkg.price.toFixed(2) }}</p>
            <p class="text-xs text-fg-muted dark:text-fg-muted-dark mt-0.5">Link expires {{ fmtDateTime(customPkg.link.link_expires_at) }}</p>
          </div>
          <div class="grid grid-cols-3 gap-2">
            <button @click="sendCustomPackage('sms')"
              :disabled="!(contact.phone && contact.sms_consent) || !!customPkg.busy"
              :title="contact.phone && contact.sms_consent ? '' : 'No SMS consent on file'"
              class="flex flex-col items-center gap-1 px-3 py-2.5 text-sm rounded-lg border border-stroke dark:border-stroke-dark text-fg-secondary dark:text-fg-secondary-dark hover:bg-surface-muted dark:hover:bg-surface-muted-dark disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              <component :is="customPkg.done.has('sms') ? CheckIcon : ChatBubbleLeftIcon" class="h-5 w-5" :class="{ 'text-green-500': customPkg.done.has('sms') }" />
              {{ customPkg.busy === 'sms' ? 'Sending…' : customPkg.done.has('sms') ? 'Texted' : 'Text' }}
            </button>
            <button @click="sendCustomPackage('email')"
              :disabled="!contact.email || !!customPkg.busy"
              :title="contact.email ? '' : 'No email on file'"
              class="flex flex-col items-center gap-1 px-3 py-2.5 text-sm rounded-lg border border-stroke dark:border-stroke-dark text-fg-secondary dark:text-fg-secondary-dark hover:bg-surface-muted dark:hover:bg-surface-muted-dark disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              <component :is="customPkg.done.has('email') ? CheckIcon : EnvelopeIcon" class="h-5 w-5" :class="{ 'text-green-500': customPkg.done.has('email') }" />
              {{ customPkg.busy === 'email' ? 'Sending…' : customPkg.done.has('email') ? 'Emailed' : 'Email' }}
            </button>
            <button @click="sendCustomPackage('copy')"
              class="flex flex-col items-center gap-1 px-3 py-2.5 text-sm rounded-lg border border-stroke dark:border-stroke-dark text-fg-secondary dark:text-fg-secondary-dark hover:bg-surface-muted dark:hover:bg-surface-muted-dark transition-colors">
              <component :is="customPkg.done.has('copy') ? CheckIcon : ClipboardDocumentIcon" class="h-5 w-5" :class="{ 'text-green-500': customPkg.done.has('copy') }" />
              {{ customPkg.done.has('copy') ? 'Copied' : 'Copy' }}
            </button>
          </div>
          <p v-if="customPkg.error" class="text-red-500 dark:text-red-400 text-sm">{{ customPkg.error }}</p>
          <button @click="customPkg = null"
            class="w-full px-4 py-2 text-sm border border-stroke dark:border-stroke-dark rounded-lg text-fg-secondary dark:text-fg-secondary-dark hover:bg-surface-muted dark:hover:bg-surface-muted-dark transition-colors">
            Done
          </button>
        </div>
      </div>
    </div>
  </Teleport>

  <!-- Hardship Offer Modal -->
  <Teleport to="body">
    <div v-if="showHardshipModal" class="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-10 px-4">
      <div class="fixed inset-0 bg-black/60" @click="showHardshipModal = false" />
      <div class="relative bg-surface dark:bg-surface-dark rounded-xl shadow-2xl w-full max-w-md">

        <!-- Header -->
        <div class="px-6 pt-6 pb-4 border-b border-stroke dark:border-stroke-dark">
          <h2 class="text-lg font-semibold text-fg-base dark:text-fg-base-dark">Send Hardship Offer</h2>
          <p class="text-sm text-fg-muted dark:text-fg-muted-dark mt-0.5">{{ contact.name }}</p>
        </div>

        <!-- Success state -->
        <div v-if="hardshipSent" class="px-6 py-8 text-center">
          <p class="text-green-500 dark:text-green-400 font-medium mb-2">Offer sent!</p>
          <p class="text-sm text-fg-muted dark:text-fg-muted-dark mb-6">
            A 72-hour offer email has been sent to {{ contact.email }}.
            A reminder will be sent automatically at 48 hours if the email hasn't been opened.
          </p>
          <button @click="showHardshipModal = false" class="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">Done</button>
        </div>

        <!-- Form -->
        <div v-else class="px-6 py-5 space-y-4">
          <!-- Balance snapshot -->
          <div class="bg-surface-muted dark:bg-surface-muted-dark rounded-lg px-4 py-3 text-sm">
            <p class="text-fg-muted dark:text-fg-muted-dark">Current balance</p>
            <p class="text-red-500 dark:text-red-400 font-semibold text-base">{{ fmtCents(contact.balance_cents) }}</p>
          </div>

          <!-- Monthly rate -->
          <div>
            <label class="block text-xs font-medium text-fg-muted dark:text-fg-muted-dark mb-1">Monthly rate (cents)</label>
            <input v-model.number="hardshipForm.offered_monthly_rate_cents" type="number" step="100"
              class="w-full px-3 py-2 text-sm rounded-lg border border-stroke dark:border-stroke-dark bg-surface dark:bg-surface-dark text-fg-base dark:text-fg-base-dark" />
            <p class="text-xs text-fg-faint dark:text-fg-faint-dark mt-0.5">${{ (hardshipForm.offered_monthly_rate_cents / 100).toFixed(0) }}/month</p>
          </div>

          <!-- Repayment -->
          <div>
            <label class="block text-xs font-medium text-fg-muted dark:text-fg-muted-dark mb-1">Monthly repayment toward balance (cents)</label>
            <input v-model.number="hardshipForm.offered_repayment_cents" type="number" step="100"
              class="w-full px-3 py-2 text-sm rounded-lg border border-stroke dark:border-stroke-dark bg-surface dark:bg-surface-dark text-fg-base dark:text-fg-base-dark" />
            <p class="text-xs text-fg-faint dark:text-fg-faint-dark mt-0.5">
              ${{ (hardshipForm.offered_repayment_cents / 100).toFixed(0) }}/mo toward balance ·
              ${{ ((hardshipForm.offered_monthly_rate_cents - hardshipForm.offered_repayment_cents) / 100).toFixed(0) }}/mo for lessons
            </p>
          </div>

          <!-- Floor / reinstatement balance -->
          <div>
            <label class="block text-xs font-medium text-fg-muted dark:text-fg-muted-dark mb-1">Reinstatement balance (floor, cents)</label>
            <input v-model.number="hardshipForm.floor_cents" type="number" step="100"
              class="w-full px-3 py-2 text-sm rounded-lg border border-stroke dark:border-stroke-dark bg-surface dark:bg-surface-dark text-fg-base dark:text-fg-base-dark" />
            <p class="text-xs text-fg-faint dark:text-fg-faint-dark mt-0.5">
              ${{ (hardshipForm.floor_cents / 100).toFixed(0) }} owed after forgiveness
            </p>
          </div>

          <!-- Cliff months -->
          <div>
            <label class="block text-xs font-medium text-fg-muted dark:text-fg-muted-dark mb-1">Forgiveness cliff (months)</label>
            <input v-model.number="hardshipForm.forgiveness_cliff_months" type="number" min="1" max="24"
              class="w-full px-3 py-2 text-sm rounded-lg border border-stroke dark:border-stroke-dark bg-surface dark:bg-surface-dark text-fg-base dark:text-fg-base-dark" />
          </div>

          <!-- Summary -->
          <div class="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-3 text-sm">
            <p class="text-blue-700 dark:text-blue-300 font-medium">
              Forgiving ${{ (hardshipForgiven / 100).toFixed(0) }} upfront —
              ${{ (hardshipForm.floor_cents / 100).toFixed(0) }} reinstatement balance
              at ${{ (hardshipForm.offered_repayment_cents / 100).toFixed(0) }}/mo clears in {{ hardshipForm.forgiveness_cliff_months }} payments
            </p>
          </div>

          <p v-if="hardshipError" class="text-red-500 dark:text-red-400 text-sm">{{ hardshipError }}</p>

          <div class="flex gap-3 pt-2">
            <button @click="showHardshipModal = false"
              class="flex-1 px-4 py-2 text-sm border border-stroke dark:border-stroke-dark rounded-lg text-fg-secondary dark:text-fg-secondary-dark hover:bg-surface-muted dark:hover:bg-surface-muted-dark transition-colors">
              Cancel
            </button>
            <button @click="sendHardshipOffer" :disabled="hardshipSending"
              class="flex-1 px-4 py-2 text-sm bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-medium disabled:opacity-50 transition-colors">
              {{ hardshipSending ? 'Sending…' : 'Send Offer' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
// Single-contact sidebar (fields, charges, lessons, reminders, activity,
// additional contacts, link/offer actions). Shown in the admin sidebar via
// SidebarContact.vue. Owns all its own per-contact state; the parent just
// supplies a serialized contact row and refetches it on `changed`.
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { useApi } from '../../composables/useApi.js'
import { Combobox, ComboboxInput, ComboboxButton, ComboboxOptions, ComboboxOption } from '@headlessui/vue'
import SidePanel from '../common/SidePanel.vue'
import ActionMenuButton from '../common/ActionMenuButton.vue'
import ActivityTimeline from '../common/ActivityTimeline.vue'
import { avatarGradient, initials } from '../../composables/useAvatarGradient.js'
import { isClosed } from '../../composables/useSubmissionStage.js'
import SignupCard from '../common/SignupCard.vue'
import AbsenceCourtesyBadge from '../common/AbsenceCourtesyBadge.vue'
import NotePromptModal from '../common/NotePromptModal.vue'
import LogTouchButtons from '../common/LogTouchButtons.vue'
import AlternateContactCard from './AlternateContactCard.vue'
import InvoiceModal from './InvoiceModal.vue'
import { canSee, findNavView } from '../../nav/adminNavItems.js'
import { useDemoMode } from '../../composables/useDemoMode'
import {
  CreditCardIcon, BanknotesIcon, CubeIcon, LifebuoyIcon,
  BellIcon, ChatBubbleLeftRightIcon, KeyIcon,
  CheckIcon, UserPlusIcon, ChatBubbleLeftIcon, EnvelopeIcon, ClipboardDocumentIcon,
  IdentificationIcon, PhoneIcon, ChevronRightIcon, MapPinIcon, ChatBubbleLeftEllipsisIcon,
  ChevronUpDownIcon, LinkIcon, GiftIcon, MusicalNoteIcon, DocumentTextIcon,
} from '@heroicons/vue/24/outline'

// Two roots (panel + teleported modal), so pass class/style through to the panel explicitly.
defineOptions({ inheritAttrs: false })

const props = defineProps({
  contact:   { type: Object, required: true },   // row shape from GET /contacts
})
const emit = defineEmits(['close', 'changed'])

const { fetchApi, logInteraction } = useApi()

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtDate = iso => {
  if (!iso) return '—'
  try {
    const [y, m, d] = iso.split('T')[0].split('-').map(Number)
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  } catch { return iso }
}
const fmtShortDate = fmtDate
const fmtDateTime = iso => new Date(iso).toLocaleString('en-US', {
  month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York',
}) + ' ET'
// UTC timestamp (naive, or with Z / +00:00) → Eastern calendar date
const fmtUtcDate = iso => new Date(/(Z|[+-]\d\d:?\d\d)$/.test(iso) ? iso : iso + 'Z')
  .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'America/New_York' })

const fmtCents = cents => {
  if (cents == null) return '—'
  const abs = Math.abs(cents)
  const fmt = (abs / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
  return cents < 0 ? `-${fmt}` : fmt
}

const fmtPaymentMethod = row => {
  if (!row.stripe_pm_last4) return null
  const brand = row.stripe_pm_brand
    ? row.stripe_pm_brand.charAt(0).toUpperCase() + row.stripe_pm_brand.slice(1)
    : row.stripe_pm_type || 'Card'
  const exp = row.stripe_pm_exp_month && row.stripe_pm_exp_year
    ? ` exp ${row.stripe_pm_exp_month}/${String(row.stripe_pm_exp_year).slice(-2)}`
    : ''
  return `${brand} ···${row.stripe_pm_last4}${exp}`
}

const fmtUnixDate = ts => {
  if (!ts) return '—'
  return new Date(ts * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const fmtLessonDateList = (dates) => {
  if (!dates?.length) return ''
  const fmt = iso => {
    const [, m, d] = iso.split('-').map(Number)
    return `${m}/${d}`
  }
  const shown = dates.slice(0, 4).map(fmt)
  const extra = dates.length > 4 ? ` +${dates.length - 4} more` : ''
  return shown.join(', ') + extra
}

const chargeStatusLabel = ch => {
  if (ch.refunded) return 'refunded'
  if (ch.amount_refunded > 0) return 'partial refund'
  return ch.status
}

const chargeStatusClass = ch => {
  if (ch.refunded || ch.amount_refunded > 0) return 'text-yellow-600 dark:text-yellow-400'
  if (ch.status === 'succeeded') return 'text-green-600 dark:text-green-400'
  if (ch.status === 'failed') return 'text-red-500 dark:text-red-400'
  return 'text-fg-muted dark:text-fg-muted-dark'
}

const billingStatusClass = status => ({
  succeeded:   'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
  failed:      'bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400',
  pending:     'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300',
  pending_pm:  'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300',
  skipped:     'bg-surface-muted dark:bg-surface-muted-dark text-fg-muted dark:text-fg-muted-dark',
}[status] || 'bg-surface-muted dark:bg-surface-muted-dark text-fg-muted dark:text-fg-muted-dark')

const attendanceClass = status => ({
  attended:  'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
  absent:    'bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400',
  excused:   'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300',
  cancelled: 'bg-surface-muted dark:bg-surface-muted-dark text-fg-muted dark:text-fg-muted-dark',
}[status?.toLowerCase()] || 'bg-surface-muted dark:bg-surface-muted-dark text-fg-muted dark:text-fg-muted-dark')

// ── Fields + activity ─────────────────────────────────────────────────────────
const panelDraft           = ref({ name: '', email: '', phone: '', aliases: '' })
const editingDetails       = ref(false)
const chargesOpen          = ref(false)
const lessonsOpen          = ref(false)
const upcomingOpen         = ref(false)
const showAllUpcoming      = ref(false)
const inquiriesOpen        = ref(true)
const remindersOpen        = ref(false)
const financialOpen        = ref(false)
const activityOpen         = ref(true)
const panelSaving          = ref(false)
const panelSaveError       = ref(null)
const panelSaveSuccess     = ref(false)
const panelActivity        = ref(null)
const panelActivityLoading = ref(false)

const upcomingLessons = computed(() => panelActivity.value?.upcoming_lessons || [])

const followUpOverdue = computed(() => {
  const due = panelActivity.value?.next_follow_up
  return !!due && due < new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' })
})

// ── Inquiries (open signups for this contact) ─────────────────────────────────
const inquiries       = ref([])
const activeInquiries = computed(() => inquiries.value.filter(r => !isClosed(r)))

async function loadInquiries(contactId) {
  try {
    inquiries.value = await fetchApi(`/api/admin/submissions?contact_id=${contactId}`)
  } catch {
    inquiries.value = []
  }
}

// ── Log Call / Add Note (note, outside text, outside email) ─────────────────────
// One contact_interactions row on this contact (same endpoint as the signup
// cards' buttons) — shows in the Activity below and in every signup's History.
// A call also marks any not-yet-called open signup as called.
const logPrompt       = ref(null) // { kind: 'call'|'text'|'email'|'note', outcome?: 'spoke'|'voicemail' }
const logPromptSaving = ref(false)
const _LOG_PROMPT_COPY = {
  note:  ['Add Note', 'What happened? e.g. parent called with a scheduling question.'],
  text:  ['Log External Text', 'A text sent from a personal phone. Optional — what was it about?'],
  email: ['Log External Email', 'An email sent from a personal mailbox. Optional — what was it about?'],
}
const logPromptTitle = computed(() => {
  const p = logPrompt.value
  const title = p?.kind === 'call'
    ? (p.outcome === 'voicemail' ? 'Log Call — Left Voicemail' : 'Log Call — Spoke')
    : _LOG_PROMPT_COPY[p?.kind]?.[0]
  return p?.recipient ? `${title} · ${p.recipient.name}` : title  // logged from an alternate contact's card
})
const logPromptSubtitle = computed(() =>
  logPrompt.value?.kind === 'call' ? 'Note is optional.' : _LOG_PROMPT_COPY[logPrompt.value?.kind]?.[1])

// Practice page (homework, plan 58): copy the student's link, or reset it so every
// earlier link stops working (e.g. one got shared somewhere it shouldn't be).
const practiceLinkCopied = ref(null)
const _copyText = async (text) => {
  try { await navigator.clipboard.writeText(text) } catch { window.prompt('Copy this link:', text) }
}
const copyPracticeLink = async (s) => {
  try {
    const { url } = await fetchApi(`/api/homework/students/${s.id}/practice-link`)
    await _copyText(url)
    practiceLinkCopied.value = s.id
    setTimeout(() => { if (practiceLinkCopied.value === s.id) practiceLinkCopied.value = null }, 2000)
  } catch (e) { alert(e.detail || 'Could not get the practice link.') }
}
const resetPracticeLink = async (s) => {
  if (!confirm(`Reset ${s.name || 'this student'}'s practice link? Every link sent so far will stop working. New homework emails will carry the new link.`)) return
  try {
    const { url } = await fetchApi(`/api/homework/students/${s.id}/reset-practice-link`, { method: 'POST' })
    await _copyText(url)
    practiceLinkCopied.value = s.id
  } catch (e) { alert(e.detail || 'Could not reset the practice link.') }
}

// Header student chips — skip the contact themselves (adult students), which
// would just repeat the name right above.
const _normName = n => (n || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase()
const headerStudents = computed(() =>
  (props.contact.students || []).filter(s => _normName(s.name) !== _normName(props.contact.name)))

// Send Text: only for opted-in contacts, and not on the Texts page itself
// (the thread is already open right next to this panel there).
const route   = useRoute()
const HEADER_ICON_BTN = 'p-1 rounded text-fg-muted dark:text-fg-muted-dark hover:text-fg-base dark:hover:text-fg-base-dark hover:bg-surface-muted dark:hover:bg-surface-hover-dark transition-colors'
const canText = computed(() => !!(props.contact.phone && props.contact.sms_consent) && route.name !== 'AdminSmsInbox')
// Header email opens a new message in the Email inbox for users who can see
// that view (same rule as the sidebar); everyone else gets a plain mailto:.
// Demo Mode: gate on the previewed user's permissions, not the real admin's.
const _user    = JSON.parse(localStorage.getItem('user') || 'null')
const _perms   = useDemoMode().permissionsFor(_user?.permissions)
const canEmail = canSee(findNavView('/admin/messages/inbox').view, _perms, _user?.tenant_id)

function startLog(entry) {
  logPrompt.value = entry
}

async function saveLogPrompt({ note, occurredAt }) {
  const { kind, outcome } = logPrompt.value
  if (kind === 'note' && !note) return
  logPromptSaving.value = true
  try {
    const { inquiries: changed } = await logInteraction(props.contact.id, {
      kind, outcome, note: note || null, occurred_at: occurredAt || null,
      recipient_id: logPrompt.value.recipient?.id ?? null,
    })
    changed.forEach(replaceInquiry)
    loadActivity(props.contact.id)
  } catch (e) {
    console.error(`Failed to log ${kind}`, e)
  } finally {
    logPromptSaving.value = false
    logPrompt.value       = null
  }
}

function replaceInquiry(updated) {
  const idx = inquiries.value.findIndex(r => r.id === updated.id)
  if (idx !== -1) inquiries.value[idx] = updated
}

async function loadActivity(contactId) {
  panelActivity.value        = null
  showAllUpcoming.value      = false
  panelActivityLoading.value = true
  try {
    panelActivity.value = await fetchApi(`/api/contacts/${contactId}/activity`)
  } catch {
    panelActivity.value = { lessons: [], charges: [] }
  } finally {
    panelActivityLoading.value = false
  }
}

const DETAIL_FIELDS = [
  { key: 'name',    label: 'Name',          type: 'text'  },
  { key: 'email',   label: 'Email',         type: 'email' },
  { key: 'phone',   label: 'Phone',         type: 'tel'   },
  { key: 'aliases', label: 'Also known as', type: 'text', placeholder: 'Nicknames, maiden names… (comma-separated)' },
]

function resetDraft() {
  const c = props.contact
  panelDraft.value = { name: c.name || '', email: c.email || '', phone: c.phone || '', aliases: (c.aliases || []).join(', ') }
  studentDrafts.value = (c.students || []).map(st => ({
    id: st.id, name: st.name || '', dob: st.dob || '', contact: { id: c.id, name: c.name, email: c.email },
  }))
}

// ── Student edits (name / DOB / linked contact) ──────────────────────────────
const studentDrafts  = ref([])
const contactOptions = ref([])
const contactQuery   = ref('')

const filteredContacts = computed(() => {
  const q = contactQuery.value.toLowerCase()
  const list = q
    ? contactOptions.value.filter(c => c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q))
    : contactOptions.value
  return list.slice(0, 50)
})

async function startEditDetails() {
  resetDraft()
  editingDetails.value = true
  if (!contactOptions.value.length && studentDrafts.value.length) {
    try { contactOptions.value = await fetchApi('/api/contacts/lookup') } catch { /* picker just stays empty */ }
  }
}

// PATCH body of only the fields that changed, or null if nothing did.
function studentChanges(draft) {
  const orig = (props.contact.students || []).find(st => st.id === draft.id) || {}
  const body = {}
  if (draft.name.trim() !== (orig.name || '')) body.name = draft.name.trim()
  if ((draft.dob || null) !== (orig.dob || null)) body.dob = draft.dob || null
  if (draft.contact?.id && draft.contact.id !== props.contact.id) body.contact_id = draft.contact.id
  return Object.keys(body).length ? body : null
}

function cancelEditDetails() {
  resetDraft()
  editingDetails.value = false
  panelSaveError.value = null
}

async function savePanel() {
  panelSaving.value      = true
  panelSaveError.value   = null
  panelSaveSuccess.value = false
  try {
    await fetchApi(`/api/contacts/${props.contact.id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        name:  panelDraft.value.name.trim(),
        email: panelDraft.value.email.trim(),
        phone: panelDraft.value.phone.trim(),
        aliases: panelDraft.value.aliases.split(',').map(a => a.trim()).filter(Boolean),
      }),
    })
    for (const draft of studentDrafts.value) {
      const body = studentChanges(draft)
      if (body) await fetchApi(`/api/students/${draft.id}`, { method: 'PATCH', body: JSON.stringify(body) })
    }
    panelSaveSuccess.value = true
    editingDetails.value   = false
    emit('changed')
    setTimeout(() => { panelSaveSuccess.value = false }, 3000)
  } catch (err) {
    panelSaveError.value = err?.message || 'Save failed.'
  } finally {
    panelSaving.value = false
  }
}

async function toggleSeriesReminder(series, field) {
  const next = !series[field]
  await fetchApi(`/api/lesson-series/${series.id}`, {
    method: 'PATCH',
    body: JSON.stringify({ [field]: next }),
  })
  series[field] = next
}

// ── Login invite ─────────────────────────────────────────────────────────────
// Run through the strip's runLink(), which owns busy/done state and errors.
async function sendLoginInvite() {
  const c = props.contact
  if (!confirm(`Send login invite to ${c.name}?`)) return false
  const data = await post('/api/auth/invite', { contact_ids: [c.id] })
  if (data.no_email?.length) alert(`${c.name} has no email on file.`)
  else if (data.errors?.length) alert(`Skipped: ${data.errors.join(', ')}`)
}

// ── Additional contacts (recipients) ─────────────────────────────────────────
const recipients        = ref([])
const recipientsLoading = ref(false)
const addingRecipient   = ref(false)
const newRecipient      = ref({ name: '', label: '', email: '', phone: '', billing_access: false })

async function loadRecipients(c) {
  if (c.recipients) { recipients.value = c.recipients; return }
  recipients.value = []
  recipientsLoading.value = true
  try {
    recipients.value = await fetchApi(`/api/contacts/${c.id}/recipients`)
  } finally {
    recipientsLoading.value = false
  }
}

// "Make primary contact" swapped the people on this same contact id, so the
// id watcher won't reload anything — refetch the contact, alternates and Activity.
async function onPrimaryChanged() {
  emit('changed')
  recipients.value = await fetchApi(`/api/contacts/${props.contact.id}/recipients`)
  loadActivity(props.contact.id)
}

const FULL_ACCESS_HINT = 'Payment emails and texts, and their account link can do everything the primary’s can: update the card, pay, buy packages, accept invites, request lessons'

const NEW_RECIPIENT_FIELDS = [
  { key: 'name',  type: 'text',  placeholder: 'Name *' },
  { key: 'label', type: 'text',  placeholder: 'Label (Dad, Grandma, Sitter…)' },
  { key: 'email', type: 'email', placeholder: 'Email' },
  { key: 'phone', type: 'tel',   placeholder: 'Phone' },
]

function startAddRecipient() {
  addingRecipient.value = true
  newRecipient.value = { name: '', label: '', email: '', phone: '', billing_access: false }
}

async function submitNewRecipient() {
  if (!newRecipient.value.name.trim()) return
  const data = await fetchApi(`/api/contacts/${props.contact.id}/recipients`, {
    method: 'POST',
    body: JSON.stringify({
      name:  newRecipient.value.name.trim(),
      label: newRecipient.value.label.trim() || null,
      email: newRecipient.value.email.trim() || null,
      phone: newRecipient.value.phone.trim() || null,
      billing_access: newRecipient.value.billing_access,
    }),
  })
  recipients.value = [...recipients.value, data]
  addingRecipient.value = false
}


// ── Bottom strip: copy / email / text link actions ──────────────────────────
// One busy key (the item running) and one done key (which button flashes green)
// for the whole strip — every action is a single request, so they never overlap.
const IDLE_BTN = 'border-stroke-strong dark:border-stroke-strong-dark text-fg-secondary dark:text-fg-secondary-dark hover:bg-surface dark:hover:bg-surface-dark'
const DONE_BTN = 'border-green-500 dark:border-green-600 text-green-600 dark:text-green-400'

const linkBusy = ref(null)
const linkDone = ref(null)

async function runLink(done, key, fn) {
  linkBusy.value = key
  try {
    if (await fn() === false) return   // cancelled at a confirm()
    linkDone.value = done
    setTimeout(() => { if (linkDone.value === done) linkDone.value = null }, 1500)
  } catch (e) {
    alert(e.message || 'Something went wrong.')
  } finally {
    linkBusy.value = null
  }
}

const post = (url, body) => fetchApi(url, { method: 'POST', ...(body && { body: JSON.stringify(body) }) })
const copyText = (url) => navigator.clipboard.writeText(url)
const contactUrl = (path) => `/api/contacts/${props.contact.id}/${path}`

function accountLink(channel, page = 'account') {
  return post(contactUrl('account-link'), { channel, page })
}

function copyAccountLink() {
  runLink('copy-account', 'copy-account', async () => copyText((await accountLink('copy')).link_url))
}

// The compact "quick" preferences page — the single SMS-consent view from our
// approved A2P campaign. Issuing a link rotates the contact's preferences token,
// so any earlier preferences link stops working.
function copySmsOptInLink() {
  runLink('copy-optin', 'copy-optin', async () =>
    copyText((await post(contactUrl('send-preferences-link'), { mode: 'quick', channel: 'copy' })).link_url))
}

const PACKAGE_LINK_DURATIONS = [
  { value: 6,   label: '6 hours' },
  { value: 12,  label: '12 hours' },
  { value: 24,  label: '24 hours' },
  { value: 48,  label: '48 hours' },
  { value: 168, label: '7 days' },
]

const isStaffContact = computed(() =>
  props.contact.permissions?.includes('teacher') || props.contact.permissions?.includes('admin'))

const copyMenu = computed(() => {
  const c = props.contact
  const item = (key, label, icon, fn, extra = {}) =>
    ({ key, label, icon, loadingLabel: 'Generating…', onClick: () => runLink('copy', key, fn), ...extra })
  return [
    { header: 'Payments', items: [
      item('copy-setup', 'Payment setup link', CreditCardIcon,
        async () => copyText((await post(`/api/billing/setup/link/${c.id}?page=setup`)).url)),
      item('copy-balance', 'Pay balance link', BanknotesIcon,
        async () => copyText((await post(`/api/billing/setup/link/${c.id}?page=balance`)).url),
        { visible: (c.balance_cents || 0) > 0 }),
      item('copy-gift', 'Gift certificate link', GiftIcon,
        async () => copyText((await accountLink('copy', 'gift')).link_url)),
    ] },
    { header: 'Lesson package', items: [
      item('copy-package', 'Package purchase link', CubeIcon,
        async () => copyText((await post(`/api/lesson-packages/admin/link/${c.id}`)).url)),
      { key: 'custom-pkg', label: 'Custom package…', icon: CubeIcon, onClick: openCustomPackage },
    ] },
    { header: 'Login', items: [
      // Minting the link clears their password — it's a reset, not just a login shortcut.
      item('copy-reset', 'Password reset link', KeyIcon,
        async () => copyText((await post(`/api/auth/magic-link/${c.id}`)).url),
        { visible: isStaffContact.value, title: 'Clears their current password' }),
    ] },
    { header: 'SMS consent', items: [
      { key: 'verbal-consent', label: 'Record verbal SMS consent…', icon: ChatBubbleLeftRightIcon,
        visible: !c.sms_consent, disabled: !c.phone || smsConsentSaving.value === c.id,
        title: c.phone ? '' : 'No phone on file', onClick: () => toggleSmsConsent(c) },
    ] },
  ]
})

// Header "Links" menu: every per-contact link, short-coded, copied on click.
const headerLinksMenu = computed(() => {
  const c = props.contact
  const item = (key, label, icon, fn, extra = {}) =>
    ({ key, label, icon, loadingLabel: 'Generating…', onClick: () => runLink('links', key, fn), ...extra })
  return [
    { items: [
      item('links-account', 'Account page', IdentificationIcon, async () => copyText((await accountLink('copy')).link_url)),
      item('links-recital', 'Recital registration', MusicalNoteIcon,
        async () => copyText((await post(`/api/recital/admin/family-link/${c.id}`)).url),
        { visible: ['admin', 'recitals'].some(p => _perms.includes(p)) }),
    ] },
    { header: 'Payments', items: [
      item('links-setup', 'Payment setup', CreditCardIcon,
        async () => copyText((await post(`/api/billing/setup/link/${c.id}?page=setup`)).url)),
      item('links-balance', 'Pay balance', BanknotesIcon,
        async () => copyText((await post(`/api/billing/setup/link/${c.id}?page=balance`)).url),
        { visible: (c.balance_cents || 0) > 0 }),
      item('links-package', prepay.value?.enabled ? 'Prepay package purchase' : 'Lesson package purchase', CubeIcon,
        async () => copyText((await post(`/api/lesson-packages/admin/link/${c.id}`)).url)),
      item('links-gift', 'Redeem gift certificate', GiftIcon, async () => copyText((await accountLink('copy', 'gift')).link_url)),
    ] },
    // Issuing a preferences link rotates the token, so earlier ones stop working.
    { header: 'Preferences', items: [
      item('links-optin', 'SMS opt-in', ChatBubbleLeftRightIcon,
        async () => copyText((await post(contactUrl('send-preferences-link'), { mode: 'quick', channel: 'copy' })).link_url),
        { visible: !c.sms_consent }),
      item('links-prefs', 'Notification preferences', BellIcon,
        async () => copyText((await post(contactUrl('send-preferences-link'), { mode: 'full', channel: 'copy' })).link_url)),
    ] },
    { header: 'Login', items: [
      item('links-reset', 'Password reset', KeyIcon,
        async () => copyText((await post(`/api/auth/magic-link/${c.id}`)).url),
        { visible: isStaffContact.value, title: 'Clears their current password' }),
    ] },
  ]
})

const emailMenu = computed(() => {
  const c = props.contact
  const item = (key, label, icon, fn, extra = {}) =>
    ({ key, label, icon, loadingLabel: 'Sending…', onClick: () => runLink('email', key, fn), ...extra })
  const confirmed = (msg, fn) => async () => confirm(msg) ? fn() : false
  return [
    { header: 'Account', items: [
      item('email-account', 'Account overview link', IdentificationIcon, () => accountLink('email')),
      item('email-optin', 'SMS opt-in link', ChatBubbleLeftRightIcon,
        confirmed(`Email an SMS opt-in link to ${c.name}?`,
          () => post(contactUrl('send-preferences-link'), { mode: 'quick' })),
        { visible: !c.sms_consent }),
      item('email-prefs', 'Notification preferences link', BellIcon,
        confirmed(`Email a notification preferences link to ${c.name}?`,
          () => post(contactUrl('send-preferences-link'), { mode: 'full' })),
        { visible: !!c.sms_consent }),
    ] },
    { header: 'Payments', items: [
      item('email-setup', 'Payment setup link', CreditCardIcon,
        () => post(`/api/billing/setup/link/${c.id}?page=setup&send_email=true`)),
      { key: 'invoice', label: 'Invoice (PDF)…', icon: DocumentTextIcon,
        visible: _perms.includes('admin'), onClick: () => { showInvoiceModal.value = true } },
      { key: 'hardship', label: 'Hardship offer…', icon: LifebuoyIcon,
        visible: (c.balance_cents || 0) > 10000 && !c.active_membership, onClick: openHardshipModal },
    ] },
    { header: 'Login', items: [
      item('email-reset', 'Password reset link', KeyIcon,
        confirmed(`Email a password reset link to ${c.name}?`,
          () => post(`/api/auth/magic-link/${c.id}?send_email=true`)),
        { visible: isStaffContact.value, title: 'Clears their current password' }),
      item('email-invite', 'Login invite', UserPlusIcon, sendLoginInvite, { visible: isStaffContact.value }),
    ] },
  ]
})

const textMenu = computed(() => {
  const c = props.contact
  return [
    { header: 'Account', items: [
      { key: 'text-account', label: 'Account overview link', icon: IdentificationIcon, loadingLabel: 'Sending…',
        onClick: () => runLink('text', 'text-account', () => accountLink('sms')) },
    ] },
    { items: [
      { key: 'revoke-consent', label: 'Revoke SMS consent', icon: ChatBubbleLeftRightIcon,
        disabled: smsConsentSaving.value === c.id, onClick: () => toggleSmsConsent(c) },
    ] },
  ]
})

// ── Custom package — admin-set count + price, one-package link ────────────────
const customPkg = ref(null)  // { size, price, hours, busy, error, link, done: Set<channel> } while the modal is open

function openCustomPackage() {
  customPkg.value = { size: null, price: null, hours: 48, busy: null, error: '', link: null, done: new Set() }
}

async function createCustomPackage() {
  const c = customPkg.value
  c.busy = 'create'
  c.error = ''
  try {
    c.link = await fetchApi('/api/lesson-packages/admin/create', {
      method: 'POST',
      body: JSON.stringify({
        contact_id: props.contact.id,
        package_size: c.size,
        custom_price_cents: Math.round(c.price * 100),
        expires_hours: c.hours,
      }),
    })
  } catch (e) {
    c.error = e.message || 'Failed to create link.'
  } finally {
    c.busy = null
  }
}

async function sendCustomPackage(channel) {
  const c = customPkg.value
  c.busy = channel
  c.error = ''
  try {
    if (channel === 'copy') {
      await navigator.clipboard.writeText(c.link.purchase_url)
    } else {
      await fetchApi('/api/lesson-packages/admin/send-link', {
        method: 'POST',
        body: JSON.stringify({ token: c.link.token, channel }),
      })
    }
    c.done = new Set(c.done).add(channel)
  } catch (e) {
    c.error = e.message || 'Failed to send.'
  } finally {
    c.busy = null
  }
}

// ── SMS consent (verbal, staff-recorded) ────────────────────────────────────
// For customers who agree over the phone/in person rather than through a
// widget or the self-service preferences link. Enabling requires the staff
// member to confirm (via the browser confirm dialog below) that they read
// the disclosure and the customer actually agreed — never a bare toggle.
const SMS_CONSENT_SCRIPT = 'We can text you lesson reminders, billing and payment ' +
  'notices, and other account updates instead of (or in addition to) email — texting ' +
  'is optional and never required. Message frequency varies and message/data rates ' +
  'may apply; you can reply STOP anytime to opt out. Would you like us to text you?'

const smsConsentSaving = ref(null)   // contact id currently saving, or null

async function toggleSmsConsent(contact) {
  const enabling = !contact.sms_consent
  const confirmed = enabling
    ? confirm(`Read this to the customer, then click OK only if they agree:\n\n"${SMS_CONSENT_SCRIPT}"`)
    : confirm(`Record that ${contact.name} opted out of SMS?`)
  if (!confirmed) return

  smsConsentSaving.value = contact.id
  try {
    await fetchApi(`/api/contacts/${contact.id}/sms-consent`, {
      method: 'PATCH',
      body: JSON.stringify({ sms_consent: enabling, attested: enabling }),
    })
    emit('changed')
  } catch (e) {
    alert(e.message || 'Failed to update SMS consent.')
  } finally {
    smsConsentSaving.value = null
  }
}

// ── Hardship offer ─────────────────────────────────────────────────────────────
const showHardshipModal  = ref(false)
const showInvoiceModal   = ref(false)
const hardshipSending    = ref(false)
const hardshipSent       = ref(false)
const hardshipError      = ref('')
const hardshipOffer      = ref(null)   // latest offer for this contact (from API)

const hardshipForm = ref({
  offered_monthly_rate_cents: 15900,
  offered_repayment_cents:    2500,
  floor_cents:                15000,
  forgiveness_cliff_months:   6,
})

const hardshipForgiven = computed(() => {
  const balance = Math.max(0, props.contact.balance_cents || 0)
  return Math.max(0, balance - hardshipForm.value.floor_cents)
})

async function openHardshipModal() {
  hardshipSent.value  = false
  hardshipError.value = ''
  hardshipForm.value  = {
    offered_monthly_rate_cents: 15900,
    offered_repayment_cents:    2500,
    floor_cents:                15000,
    forgiveness_cliff_months:   6,
  }
  showHardshipModal.value = true
  try {
    const data = await fetchApi('/api/admin/membership-settings')
    hardshipForm.value = {
      offered_monthly_rate_cents: data.effective.hardship_offered_monthly_rate_cents,
      offered_repayment_cents:    data.effective.hardship_offered_repayment_cents,
      floor_cents:                data.effective.hardship_floor_cents,
      forgiveness_cliff_months:   data.effective.hardship_forgiveness_cliff_months,
    }
  } catch {
    // keep the hardcoded fallback already set above
  }
}

async function sendHardshipOffer() {
  hardshipSending.value = true
  hardshipError.value   = ''
  try {
    await fetchApi('/api/hardship/send', {
      method: 'POST',
      body: JSON.stringify({
        contact_id:                 props.contact.id,
        offered_monthly_rate_cents: hardshipForm.value.offered_monthly_rate_cents,
        offered_repayment_cents:    hardshipForm.value.offered_repayment_cents,
        floor_cents:                hardshipForm.value.floor_cents,
        forgiveness_cliff_months:   hardshipForm.value.forgiveness_cliff_months,
      }),
    })
    hardshipSent.value = true
    // Reload the offer status for this contact
    fetchHardshipOffer(props.contact.id)
  } catch (e) {
    hardshipError.value = e.message || 'Failed to send offer.'
  } finally {
    hardshipSending.value = false
  }
}

async function fetchHardshipOffer(contactId) {
  try {
    const data = await fetchApi(`/api/hardship/admin/contact/${contactId}`)
    hardshipOffer.value = data
  } catch {
    hardshipOffer.value = null
  }
}

const packageCredit = ref(null)   // remaining lesson-package credit for this contact (from API)

// ── Pricing: assisted rate + package rate (admin + signups; a 403 hides it) ──
const pricing = ref(null)
const packageRate = ref(null)     // dollars; the same field prepay-only uses
const pricingSaving = ref(false)
const pricingError = ref('')

function setPricing(data) {
  pricing.value = data
  packageRate.value = data?.package_rate_cents ? data.package_rate_cents / 100 : null
}

async function fetchPricing(contactId) {
  try {
    setPricing(await fetchApi(`/api/contacts/${contactId}/pricing`))
  } catch {
    setPricing(null)
  }
}

async function savePricing(change) {
  pricingSaving.value = true
  pricingError.value = ''
  try {
    setPricing(await fetchApi(`/api/contacts/${props.contact.id}/pricing`, {
      method: 'PUT',
      body: JSON.stringify(change),
    }))
    if ('package_rate_cents' in change) fetchPrepay(props.contact.id)
  } catch (e) {
    pricingError.value = e.message || 'Failed to save.'
  } finally {
    pricingSaving.value = false
  }
}

// ── Prepay-only (admin only; a 403 just hides the section) ──────────────────
const prepay = ref(null)
const prepayPrice = ref(null)     // dollars, the contact's 4+-lesson rate
const prepaySaving = ref(false)
const prepayError = ref('')
const prepayCopied = ref(false)

function setPrepay(data) {
  prepay.value = data
  prepayPrice.value = data?.lesson_price_cents ? data.lesson_price_cents / 100 : null
}

async function fetchPrepay(contactId) {
  try {
    setPrepay(await fetchApi(`/api/contacts/${contactId}/prepay`))
  } catch {
    setPrepay(null)
  }
}

async function savePrepay(enabled) {
  const turningOn = enabled && !prepay.value.enabled
  if (turningOn && !confirm(
    `Turn on prepay-only for ${props.contact.name}?\n\nIn 10 days, their lessons must be paid from a package at least 5 days ahead, ` +
    `or they come off the schedule.\n\nAutomatic notices to the family aren't built yet — tell them directly.`)) return
  if (!enabled && !confirm(`Turn off prepay-only for ${props.contact.name}? Lessons go back to normal billing.`)) return
  prepaySaving.value = true
  prepayError.value = ''
  try {
    setPrepay(await fetchApi(`/api/contacts/${props.contact.id}/prepay`, {
      method: 'PUT',
      body: JSON.stringify({ enabled, lesson_price_cents: enabled ? Math.round(prepayPrice.value * 100) : null }),
    }))
    fetchPricing(props.contact.id)   // prepay sets/clears the same package rate
  } catch (e) {
    prepayError.value = e.message || 'Failed to save.'
  } finally {
    prepaySaving.value = false
  }
}

async function copyPrepayLink() {
  await navigator.clipboard.writeText(prepay.value.purchase_url)
  prepayCopied.value = true
  setTimeout(() => { prepayCopied.value = false }, 2000)
}

// ── Settlement offer (admin only; a 403 hides the section) ──────────────────
const settlement = ref(null)
const settlementSaving = ref(false)
const settlementError = ref('')
const settlementCopied = ref(false)

async function fetchSettlement(contactId) {
  try {
    settlement.value = await fetchApi(`/api/financial/settlement/${contactId}`)
  } catch {
    settlement.value = null
  }
}

async function createSettlement() {
  const s = settlement.value
  if (!confirm(`Offer ${props.contact.name} a one-time payoff of ${fmtCents(s.settle_cents)} (instead of ${fmtCents(s.full_cents)}) until ` +
    `${fmtDate(s.until)}?\n\nLate fees are waived while it's open. Nothing is emailed — copy the link into your own email. ` +
    `Paying it switches them to prepay-only. One offer per case.`)) return
  settlementSaving.value = true
  settlementError.value = ''
  try {
    settlement.value = await fetchApi(`/api/financial/settlement/${props.contact.id}`, { method: 'POST' })
  } catch (e) {
    settlementError.value = e.message || 'Failed to create the offer.'
  } finally {
    settlementSaving.value = false
  }
}

async function copySettlementLink() {
  await navigator.clipboard.writeText(settlement.value.pay_url)
  settlementCopied.value = true
  setTimeout(() => { settlementCopied.value = false }, 2000)
}

// ── Discounts (admin + signups; a 403 hides the section) ────────────────────
const discounts = ref(null)        // this contact's pending (unapplied) discounts
const discountLessons = ref([])    // unbilled lessons a discount can be pinned to
const discountFormOpen = ref(false)
const discountForm = ref({ lessonId: null, amount: '', reason: '' })
const discountSaving = ref(false)
const discountError = ref('')

async function fetchDiscounts(contactId) {
  try {
    const rows = await fetchApi(`/api/financial/discounts?contact_id=${contactId}`)
    discounts.value = rows.filter(d => !d.applied_at)
  } catch {
    discounts.value = null
  }
}

async function openDiscountForm() {
  discountForm.value = { lessonId: null, amount: '', reason: '' }
  discountError.value = ''
  discountFormOpen.value = true
  try {
    discountLessons.value = await fetchApi(`/api/financial/discounts/contacts/${props.contact.id}/lessons`)
  } catch {
    discountLessons.value = []
  }
}

// Picking a lesson pre-fills its full price (editable)
function onDiscountLessonSelect() {
  const l = discountLessons.value.find(x => x.id === discountForm.value.lessonId)
  if (l?.price_cents != null && !discountForm.value.amount) discountForm.value.amount = (l.price_cents / 100).toFixed(2)
}

async function saveDiscount() {
  const amount_cents = Math.round(parseFloat(discountForm.value.amount) * 100)
  if (isNaN(amount_cents) || amount_cents <= 0) {
    discountError.value = 'Enter a valid amount.'
    return
  }
  discountSaving.value = true
  discountError.value = ''
  try {
    await fetchApi('/api/financial/discounts', {
      method: 'POST',
      body: JSON.stringify({
        contact_id: props.contact.id,
        amount_cents,
        reason: discountForm.value.reason || null,
        lesson_id: discountForm.value.lessonId,
      }),
    })
    discountFormOpen.value = false
    fetchDiscounts(props.contact.id)
  } catch (e) {
    discountError.value = e.message || 'Failed to save discount.'
  } finally {
    discountSaving.value = false
  }
}

async function cancelDiscount(d) {
  if (!confirm(`Cancel the ${fmtCents(d.amount_cents)} discount for ${props.contact.name}?`)) return
  discountError.value = ''
  try {
    await fetchApi(`/api/financial/discounts/${d.id}`, { method: 'DELETE' })
    fetchDiscounts(props.contact.id)
  } catch (e) {
    discountError.value = e.message || 'Failed to cancel discount.'
  }
}

async function fetchPackageCredit(contactId) {
  try {
    const data = await fetchApi(`/api/lesson-packages/admin/contact/${contactId}`)
    packageCredit.value = data
  } catch {
    packageCredit.value = null
  }
}

// Reset everything per-contact whenever a different contact is shown. A
// refetch of the *same* contact (after save etc.) keeps drafts/activity.
watch(() => props.contact.id, () => {
  const c = props.contact
  resetDraft()
  editingDetails.value   = false
  panelSaveError.value   = null
  panelSaveSuccess.value = false
  addingRecipient.value  = false
  loadRecipients(c)
  loadActivity(c.id)
  inquiries.value = []
  loadInquiries(c.id)
  hardshipOffer.value = null
  fetchHardshipOffer(c.id)
  packageCredit.value = null
  fetchPackageCredit(c.id)
  setPrepay(null)
  prepayError.value = ''
  fetchPrepay(c.id)
  settlement.value = null
  settlementError.value = ''
  fetchSettlement(c.id)
  setPricing(null)
  pricingError.value = ''
  fetchPricing(c.id)
  discounts.value = null
  discountFormOpen.value = false
  discountError.value = ''
  fetchDiscounts(c.id)
}, { immediate: true })
</script>
